import { useRef, useCallback, ChangeEvent } from 'react';

// components
import { Button, ButtonProps } from '@/components/atoms/Button/Button';

/**
 * UploadFileButton allows the user to upload a local file, accepting a
 * callback for the file content.
 */
export const UploadFileButton = ({
  variant = 'secondary',
  isFullWidth = true,
  onPress,
  onChange,
  children,
  ...props
}: UploadFileButtonProps) => {
  // refs
  const uploadRef = useRef<HTMLInputElement>(null);

  // methods
  const handleUploadCsv = useCallback(() => {
    if (!uploadRef?.current) return;

    uploadRef.current.value = '';
    uploadRef.current.click();
    onPress?.();
  }, [uploadRef, onPress]);

  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e?.target?.files) return;

      const file = e.target.files[0];
      const reader = new FileReader();

      reader.addEventListener(
        'load',
        () => reader.result && onChange?.(reader.result as string),
        false,
      );

      if (file) {
        reader.readAsText(file);
      }
    },
    [onChange],
  );

  return (
    <>
      <Button
        variant={variant}
        onPress={handleUploadCsv}
        isFullWidth={isFullWidth}
        {...props}
      >
        {children}
      </Button>
      <HiddenInput
        type='file'
        accept='.csv'
        ref={uploadRef}
        onChange={handleOnChange}
      />
    </>
  );
};

const HiddenInput = ({ ref, ...props }: any) => (
  <input ref={ref} {...props} style={{ display: 'none' }} />
);

interface UploadFileButtonProps extends ButtonProps {
  onChange: (content: string) => void;
}

UploadFileButton.componentName = 'UploadFileButton';

export default UploadFileButton;
